/**
 * Eight ways to be a nuisance, and not one of them is a scripted scene.
 *
 * Every row here is a thing you pick up somewhere real and a *shape* it uses,
 * and the shapes are the four the game already has: something you carry,
 * something you throw, something you leave on a floor, and something you do to
 * a person. What each one costs, who notices, and what they do about it are
 * answered by `perception.js`, `suspicion.js` and the `reacts` line on
 * somebody's row, the same as a stolen linen or a smashed bottle.
 *
 * Which is the whole rule this file is written to: **unwitnessed is free.**
 * Not "cheap" and not "usually" — a prank nobody saw leaves no claim, and a
 * prank with no claim behind it cannot reach your card, your standing or
 * anybody's opinion of you. There is no second path. That is also the one
 * thing worth checking on every one of them, and `scripts/pranks.mjs` does.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPES
 *
 *   carried   it goes in your hands from a stock point, like a linen does, and
 *             being seen carrying it is the same question as being seen
 *             carrying anything.
 *   thrown    it goes through `throwing.launch` and lands. A rubber band and a
 *             party popper are one row of numbers apart.
 *   left      it is put on a floor and it stays there, doing its one thing to
 *             whoever comes past.
 *   done      it happens to a person you are standing behind.
 *   doctored  it happens to a *thing*, now, and goes off later.
 *
 * `from` is where it is stocked and it is not decoration: the ice is at the ice
 * machine, the rubber band is on the manager's desk, the shrimp is in the
 * walk-in. Half of what makes a prank a decision is the walk to fetch it.
 */

/** How many of a thing you may be holding. One armful, like everything else. */
export const PRANK_STACK = 6;

export const PRANKS = [
  /**
   * Ice down the shirt, from behind.
   *
   * The purest statement of the rule in the file: it is an act aimed at one
   * person, and the only question is whether *that* person can see you when
   * you do it. Unseen by them, there is nobody who can say it was you — they
   * jump, they turn round, and they are looking at a corridor. Anybody else in
   * the room still has eyes, which is the interesting way to get caught.
   *
   * What they do about it is whatever their row says they do about anything.
   * Nothing here is written for the prank.
   */
  {
    id: 'ice',
    name: 'Handful of Ice',
    shape: 'done',
    verb: 'drop ice down',
    from: 'ice-machine',
    reach: 6,
    /** Seconds of standing there shouting, which is a `hold` and nothing new. */
    flinch: 3,
    /** How loud the jump is, in feet. People two rooms away do not hear a yelp. */
    noise: 22,
  },

  /**
   * The rubber band, off the manager's desk.
   *
   * Unlimited, because a rubber band is; quiet, because the noise a thing makes
   * falls out of its mass and this one weighs two grams; and weak, because the
   * impact energy does too. Not one of those three is a rule written here —
   * they are all consequences of the row in `objects.js`, which is the point.
   * The only rule this shape adds is that firing one does not use one up.
   */
  {
    id: 'band',
    name: 'Rubber Band',
    shape: 'thrown',
    verb: 'ping one at',
    from: 'office-desk',
    kind: 'band',
    endless: true,
  },

  /**
   * The party popper. A noise somewhere you are not.
   *
   * The distraction, and it works by the one hook the game already has for it:
   * a noise on the bus at a position, and `goAndLook` for anybody who heard it.
   * That is exactly what a suspicious NPC does when they lose sight of you, so
   * a popper is not a new behaviour — it is the existing one, asked for on
   * purpose, from across the room.
   */
  {
    id: 'popper',
    name: 'Party Popper',
    shape: 'thrown',
    verb: 'lob a popper at',
    from: 'store-linen',
    kind: 'popper',
    /** Feet. Heard across a ballroom, which is the entire point of it. */
    noise: 90,
    /** How far somebody will come to look. Beyond this they just look up. */
    draws: 70,
  },

  /**
   * A bucket of ice on a floor.
   *
   * One rule: anybody who walks through it goes over and drops what they were
   * carrying. "Drops what they carry" is `carry.jostle()`, which is the same
   * call a javelina hitting you makes — so a server who slips loses part of a
   * load exactly the way the player does, and the grip a work class buys works
   * against both.
   */
  {
    id: 'ice-spill',
    name: 'Spilled Ice',
    shape: 'left',
    verb: 'tip the ice out',
    from: 'ice-machine',
    /** Feet across. A patch, not a rink. */
    radius: 5,
    /** Game minutes before somebody has dealt with it. */
    melts: 40,
    /** Seconds on the floor. Long enough to be embarrassing. */
    down: 4,
  },

  /**
   * Pepper in a napkin.
   *
   * Handed over or left on a setting, and then they cannot see for a minute.
   * Reduced vision is a smaller cone, which is a number perception already
   * reads off `data.vision` every frame — so this is the one prank that makes
   * the *game* easier rather than making somebody unhappy, and it is the one
   * worth walking to the kitchen for.
   */
  {
    id: 'pepper',
    name: 'Peppered Napkin',
    shape: 'done',
    verb: 'hand the napkin to',
    from: 'kitchen-pass',
    reach: 7,
    /** Seconds of it. */
    lasts: 25,
    /** What is left of their sight while it lasts. */
    sight: 0.35,
    /** How often the sneeze knocks something out of their hands. Seconds. */
    every: 6,
    noise: 26,
  },

  /**
   * Old shrimp, behind something, in a room that is about to be used.
   *
   * The slow one. A smell that starts at nothing and grows, and at each stage
   * more of the room is inside it; anybody who ends up inside leaves, and a
   * guest who leaves complains. Nothing here decides that a guest complains —
   * `reacts: 'complains'` on their row does, and it did before this existed.
   */
  {
    id: 'shrimp',
    name: 'Yesterday\'s Shrimp',
    shape: 'left',
    verb: 'tuck the shrimp behind',
    from: 'kitchen-walkin',
    /** Feet it reaches when new, and when it has had an hour. */
    radius: 4,
    grows: 34,
    /** Game minutes to get there. */
    ripens: 60,
    /** And how long before somebody finds it. */
    melts: 240,
  },

  /**
   * Salt in the sugar caddy.
   *
   * The only one that is not in the room when it goes off, which is the whole
   * design: the complaint lands on the Kitchen, because a kitchen is what
   * sends out a sugar caddy, *unless* there is a claim on the record saying who
   * was standing at the caddy. Attribution is not a special case written for
   * this — it is the ordinary question of whether anybody saw you, asked later
   * instead of now.
   */
  {
    id: 'salt',
    name: 'Salt',
    shape: 'doctored',
    verb: 'salt',
    from: 'kitchen-pass',
    /** What it goes into, which is a real thing standing in the break room. */
    into: 'break-coffee',
    reach: 6,
    /** Game minutes until the next coffee service finds it. */
    delay: 45,
    /** What the complaint costs whoever ends up wearing it. */
    cost: -9,
  },

  /**
   * A note on somebody's back.
   *
   * Costs the target nothing at all, which is stated rather than implied: no
   * standing, no card, no vision, nothing. It is a sight in the world, drawn on
   * a person, and every witness forms a claim about it the way they form a
   * claim about a javelina by the dock doors — that path already exists and is
   * exactly this shape.
   *
   * The cost is yours and it is personal. If the target ends up holding a claim
   * that names you, they think less of you, in the ledger that is theirs rather
   * than their department's. Which is the right currency for a joke.
   */
  {
    id: 'note',
    name: 'Kick-Me Note',
    shape: 'done',
    verb: 'stick a note on',
    from: 'office-desk',
    reach: 5,
    /** Game minutes before it falls off or somebody tells them. */
    lasts: 90,
    /** What it costs you, personally, with the person wearing it. */
    regard: -14,
  },
];

/**
 * What each prank is, as a thing in `objects.js`.
 *
 * Every one of them is an ordinary carryable with a mass, so a prank goes in
 * your hands the way a linen does and swaps out whatever you were holding.
 * That cost is not a rule written anywhere: it is what hands are, and it is
 * why walking to the ice machine mid-setup is a decision.
 */
export const PRANK_KIND = Object.freeze({
  ice: 'ice',
  band: 'band',
  popper: 'popper',
  'ice-spill': 'ice',
  pepper: 'napkin',
  shrimp: 'shrimp',
  salt: 'salt',
  note: 'note',
});

export const PRANK_IDS = Object.freeze(PRANKS.map((entry) => entry.id));

export const byId = Object.freeze(Object.fromEntries(PRANKS.map((entry) => [entry.id, entry])));

/**
 * Where each one is stocked, as a prop in the building and a line for the
 * prompt.
 *
 * Separate from the rows above because these are facts about the *level* and
 * the rows are facts about the *prank*: move the ice machine and one line here
 * changes. `props` names a prop id that already exists in `level.js`, except
 * the ice machine, which is the one thing this pass adds to the building.
 */
export const PRANK_STOCK = [
  {
    id: 'ice-machine',
    prop: 'break-ice',
    label: 'Ice Machine',
    stocks: ['ice', 'ice-spill'],
  },
  {
    id: 'office-desk',
    prop: 'office-desk',
    label: "Manager's Desk",
    stocks: ['band', 'note'],
  },
  {
    id: 'kitchen-pass',
    prop: 'kitchen-pass',
    label: 'Kitchen Pass',
    stocks: ['pepper', 'salt'],
  },
  {
    id: 'kitchen-walkin',
    prop: 'kitchen-walkin-w',
    label: 'Walk-In',
    stocks: ['shrimp'],
  },
  {
    id: 'store-linen',
    prop: 'store-linen',
    label: 'Banquet Storage',
    stocks: ['popper'],
  },
];

export default PRANKS;
